import {
  Component,
  input,
  OnInit,
  output,
  TemplateRef,
  viewChild,
  signal
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';

import { IrisButtonContainerAlignments, IrisButtonTypes, IrisButtonIconPositions } from '@sondermind/iris/button';
import { IconSizes, IconColors } from '@sondermind/iris/icon';
import { IrisButtonTypeAttributeOptions } from '@sondermind/iris/shared';

import {
  OctaveHttpService,
  IOctaveDetails,
  IOctaveUpsertPatientRequest
} from '@sondermind/data-access/client-facing/billing';
import { InfoLinks } from '@sondermind/core/enums/info-links.enum';

@Component({
  selector: 'client-setup-ebcg-payment-info',
  templateUrl: './setup-ebcg-payment-info.component.html',
  styleUrls: ['./setup-ebcg-payment-info.component.scss'],
  standalone: false
})
export class SetupEbcgPaymentInfoComponent implements OnInit {
  readonly useInSessionRequest = input<boolean>(false);
  readonly isMobile = input<boolean>(false);
  readonly fromDirectBooking = input<boolean>(false);
  readonly providerName = input<string>('[Provider Name]');
  readonly paymentInfoSubmitted = output();

  // Template for the action button that parent component can render
  readonly actionButtonTemplate = viewChild.required<TemplateRef<unknown>>('actionButton');

  readonly ebcgTermsOfService = InfoLinks.EVERNORTH_TERMS_OF_SERVICE;
  readonly ebcgPrivacyPolicy = InfoLinks.EVERNORTH_PRIVACY_POLICY;

  readonly submitting = signal<boolean>(false);
  readonly octaveDetails = signal<IOctaveDetails | null>(null);
  readonly loading = signal<boolean>(false);

  IrisButtonContainerAlignments = IrisButtonContainerAlignments;
  IrisButtonTypes = IrisButtonTypes;
  IrisButtonTypeAttributeOptions = IrisButtonTypeAttributeOptions;
  IrisButtonIconPositions = IrisButtonIconPositions;
  IrisIconSizes = IconSizes;
  IconColors = IconColors;

  ebcgAgreementForm!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private octaveHttpService: OctaveHttpService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadOctaveDetails();
  }

  /**
   * Load Octave payment details from the backend
   */
  loadOctaveDetails(): void {
    this.loading.set(true);
    this.octaveHttpService.getDetails$().subscribe({
      next: (details) => {
        this.octaveDetails.set(details);
        this.loading.set(false);
      },
      error: () => {
        // TODO: Handle error appropriately (e.g., show error message to user)
        this.loading.set(false);
      }
    });
  }

  initializeForm(): void {
    this.ebcgAgreementForm = this.fb.group({
      acceptedEbcgTerms: [false]
    });
  }

  /**
   * Enable submit button when client has checked box accepting EBCG terms
   */
  get allowSubmit(): boolean {
    return this.ebcgAgreementForm.value.acceptedEbcgTerms &&
      !this.submitting() &&
      !this.loading();
  }

  /**
   * Open external payment provider link
   */
  openPaymentProvider(): void {
    if (!this.allowSubmit) {
      return;
    }

    this.submitting.set(true);

    // Get the EBCG billing portal URL from octaveDetails (loaded via getDetails$)
    const ebcgPaymentUrl = this.octaveDetails()?.ebcgBillingPortalUrl;

    if (ebcgPaymentUrl) {
      // Open in new window immediately
      window.open(ebcgPaymentUrl, '_blank');
    }

    // Redirect URL is where the client will be redirected after completing payment on the third-party site
    const redirectUrl = `${window.location.origin}/dashboard`;

    const requestBody: IOctaveUpsertPatientRequest = {
      redirectUrl,
      signedEbcgTos: this.ebcgAgreementForm.value.acceptedEbcgTerms
    };

    // Call upsertPatient to update the record, but don't wait for it to complete
    // The user will be on the new tab completing their payment
    this.octaveHttpService.upsertPatient$(requestBody).subscribe({
      next: () => {
        this.submitting.set(false);
        // TODO: In a future story, add a waiting page to poll/check if payment is complete
        // Once confirmed, emit paymentInfoSubmitted to notify parent component
      },
      error: () => {
        // TODO: Handle error appropriately (e.g., show error message to user)
        this.submitting.set(false);
      }
    });
  }
}
